import axios from "axios";
import { API_BASE } from "../../config";
import { trackAxios } from "../Loader/loadingTracker";
import { attachTimeZone } from "../../utils/timeZone";

// Authenticated GET calls to the new server/routes/reportsData.js endpoints
// (auth-protected, see server/index.js's "/api/reports" mount) - built on
// the same axios-with-Bearer-token instance client/src/api/index.js already
// uses for every other authenticated call, rather than the bare axios the
// existing PDF create/fetch routes use (those routes have no auth
// middleware - a pre-existing gap, left alone here since fixing it is a
// separate concern from adding new endpoints).
const API = axios.create({ baseURL: API_BASE });
// Drives the top progress bar (components/Loader/TopProgressBar.js).
trackAxios(API);
attachTimeZone(API);
API.interceptors.request.use((req) => {
  if (localStorage.getItem("profile")) {
    req.headers.authorization = `Bearer ${JSON.parse(localStorage.getItem("profile")).token}`;
  }
  return req;
});

function toQuery(params) {
  const search = new URLSearchParams();
  Object.entries(params || {}).forEach(([key, value]) => {
    if (value === null || value === undefined || value === "") return;
    search.set(key, value instanceof Date ? value.toISOString() : value);
  });
  const qs = search.toString();
  return qs ? `?${qs}` : "";
}

const get = (path, params) => API.get(`/api/reports/${path}${toQuery(params)}`);

export const fetchSalesReportData = (params) => get("sales", params);
export const fetchPurchaseReportData = (params) => get("purchases", params);
export const fetchStockAdjustmentReportData = (params) => get("stock-adjustments", params);
export const fetchCommissionReportData = (params) => get("commission", params);
export const fetchDiscountReportData = (params) => get("discount", params);
export const fetchItemSalesReportData = (params) => get("item-sales", params);
export const fetchPaymentModeSummaryData = (params) => get("payment-mode-summary", params);
export const fetchTaxSummaryData = (params) => get("tax-summary", params);
export const fetchProfitLossData = (params) => get("profit-loss", params);
export const fetchLowStockData = (params) => get("low-stock", params);
// Batch & Expiry: one row per batch still in stock, soonest expiry first.
export const fetchExpiryData = (params) => get("expiry", params);
// Batch & Expiry: item-wise batch stock (an item's batches grouped together, paged by item).
export const fetchBatchStockData = (params) => get("batch-stock", params);

// Everything a filtered report matches, fetched page by page from the server (never one huge
// response), for PDF / Excel exports. Stops at `maxPages`; `truncated` says if it did.
export const fetchAllPages = async (fetchFn, params, { pageSize = 500, maxPages = 40 } = {}) => {
  let page = 1;
  let rows = [];
  let items = [];
  let summary = null;
  let totalCount = Infinity;
  let more = true;
  while (more && page <= maxPages) {
    const { data } = await fetchFn({ ...params, page, limit: pageSize });
    const gotRows = data.rows || [];
    const gotItems = data.items || [];
    rows = rows.concat(gotRows);
    items = items.concat(gotItems);
    summary = data.summary || summary;
    totalCount = data.totalCount ?? rows.length;
    const received = gotItems.length || gotRows.length; // paged by item (batch stock) or by row (expiry)
    more = received > 0 && page * pageSize < totalCount;
    page += 1;
  }
  return { rows, items, summary, totalCount, truncated: more };
};
export const fetchCustomerLedgerData = (params) => get("customer-ledger", params);
export const fetchCustomerDueDetailData = (params) => get("customer-due-detail", params);
export const fetchSupplierDueDetailData = (params) => get("supplier-due-detail", params);
export const fetchStaffPerformanceData = (params) => get("staff-performance", params);
export const fetchShiftSummaryData = (params) => get("shift-summary", params);
// Type-ahead for the customer multi-select filter (CustomerMultiSelect.js) -
// searched server-side, so nothing is preloaded however many customers exist.
export const fetchReportCustomerOptions = (params) => get("customer-options", params);
